import React, { useEffect, useState } from 'react';
import { 
  Form, 
  Input, 
  InputNumber, 
  Select, 
  Button, 
  message, 
  Space,
  Divider,
  Tooltip,
  Spin
} from 'antd';
import { 
  SaveOutlined, 
  ArrowLeftOutlined,
  QuestionCircleOutlined
} from '@ant-design/icons';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { PageTitle, StyledCard, FormLabel, SearchableSelect } from '../common';
import { theme } from '../../theme';

// Interfaces
interface RetencionFormData {
  descripcion: string;
  porcentaje: number;
  portonelada: number;
  tipo: string;
  minimo_porton: number;
  minimo_pesos: number;
  acumulado_ganancia: number;
  provincia: number | null;
  aplicar: string;
  alta: number;
}

interface Provincia {
  id: number;
  provincia: string;
}

interface Retencion {
  id: number;
  descripcion: string;
  porcentaje: number;
  portonelada: number;
  tipo: string;
  minimo_porton: number;
  minimo_pesos: number;
  acumulado_ganancia: number;
  provincia: number | null;
  provincia_nombre: string | null;
  aplicar: string;
  alta: number;
  empresa: number;
  empresa_nombre: string;
}

const EditarRetenciones: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [loadingProvincias, setLoadingProvincias] = useState(false);
  const [provincias, setProvincias] = useState<Provincia[]>([]);
  const [retencion, setRetencion] = useState<Retencion | null>(null);
  const { getAccessTokenSilently, user } = useAuth0();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  // Cargar datos al inicializar
  useEffect(() => {
    if (user?.id_empresa && id) {
      fetchRetencion();
      fetchProvincias();
    }
  }, [user, id]);

  const fetchRetencion = async () => {
    if (!user?.id_empresa || !id) return;
    
    setLoadingData(true);
    try {
      const token = await getAccessTokenSilently();
      const response = await axios.get<Retencion>(
        `${process.env.REACT_APP_LIQUIDACION_URL}/obtener_retencion/${user.id_empresa}/${id}/`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      
      setRetencion(response.data);
      
      // Cargar datos en el formulario
      form.setFieldsValue({
        descripcion: response.data.descripcion,
        porcentaje: response.data.porcentaje,
        portonelada: response.data.portonelada,
        tipo: response.data.tipo,
        minimo_porton: response.data.minimo_porton,
        minimo_pesos: response.data.minimo_pesos,
        acumulado_ganancia: response.data.acumulado_ganancia,
        provincia: response.data.provincia,
        aplicar: response.data.aplicar,
        alta: response.data.alta
      });
      
    } catch (error: any) {
      console.error('Error al cargar retención:', error);
      message.error('Error al cargar los datos de la retención');
      navigate('/liquidacion/retenciones');
    } finally {
      setLoadingData(false);
    }
  };

  const fetchProvincias = async () => {
    setLoadingProvincias(true);
    try {
      const token = await getAccessTokenSilently();
      const response = await axios.get<Provincia[]>(
        `${process.env.REACT_APP_COMMON_URL}/listar_provincias/`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      setProvincias(response.data);
    } catch (error: any) {
      console.error('Error al cargar provincias:', error);
      message.error('Error al cargar la lista de provincias');
    } finally {
      setLoadingProvincias(false);
    }
  };

  const handleSubmit = async (values: RetencionFormData) => {
    if (!user?.id_empresa || !id) {
      message.error('No se pudo obtener la información necesaria');
      return;
    }

    setLoading(true);
    try {
      const token = await getAccessTokenSilently();
      
      const retencionData = {
        ...values,
        // Convertir strings a números para campos numéricos
        porcentaje: values.porcentaje ? parseFloat(values.porcentaje.toString()) : 0,
        portonelada: values.portonelada ? parseFloat(values.portonelada.toString()) : 0,
        minimo_porton: values.minimo_porton ? parseFloat(values.minimo_porton.toString()) : 0,
        minimo_pesos: values.minimo_pesos ? parseFloat(values.minimo_pesos.toString()) : 0,
        acumulado_ganancia: values.acumulado_ganancia ? parseFloat(values.acumulado_ganancia.toString()) : 0,
      };

      console.log('Datos enviados:', retencionData);

      const response = await axios.put(
        `${process.env.REACT_APP_LIQUIDACION_URL}/editar_retencion/${user.id_empresa}/${id}/`,
        retencionData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      message.success('Retención actualizada exitosamente');
      navigate('/liquidacion/retenciones');
      
    } catch (error: any) {
      console.error('Error al actualizar retención:', error);
      if (error.response?.data?.error) {
        message.error(`Error: ${error.response.data.error}`);
      } else {
        message.error('Error al actualizar la retención');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVolver = () => {
    navigate('/liquidacion/retenciones');
  };

  const handleLimpiar = () => {
    if (retencion) {
      form.setFieldsValue({
        descripcion: retencion.descripcion,
        porcentaje: retencion.porcentaje,
        portonelada: retencion.portonelada,
        tipo: retencion.tipo,
        minimo_porton: retencion.minimo_porton,
        minimo_pesos: retencion.minimo_pesos,
        acumulado_ganancia: retencion.acumulado_ganancia,
        provincia: retencion.provincia,
        aplicar: retencion.aplicar,
        alta: retencion.alta
      });
    }
  };

  if (loadingData) {
    return (
      <div style={{ padding: '0', display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <Spin size="large" tip="Cargando datos de la retención..." />
      </div>
    );
  }

  if (!retencion) {
    return (
      <div style={{ padding: '0' }}>
        <PageTitle>Editar Retención</PageTitle>
        <StyledCard title="Error">
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div style={{ marginBottom: '16px', color: theme.colors.textSecondary }}>
              No se encontró la retención solicitada
            </div>
            <Button onClick={handleVolver} style={{ color: theme.colors.textSecondary, borderColor: theme.colors.border }}>
              Volver al Listado
            </Button>
          </div>
        </StyledCard>
      </div>
    );
  }

  return (
    <div style={{ padding: '0' }}>
      <PageTitle style={{ marginBottom: '12px', fontSize: '24px' }}>Editar Retenciones/Percepciones</PageTitle>

      <StyledCard title="Información de la Retención">
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          style={{ 
            maxWidth: '100%',
            padding: '12px'
          }}
          className="retenciones-form"
        >
          <style>{`
            .retenciones-form .ant-form-item {
              margin-bottom: 8px !important;
            }
            .retenciones-form .ant-form-item-label > label {
              color: ${theme.colors.textPrimary} !important;
              font-weight: 500 !important;
              font-size: 12px !important;
              line-height: 1.3 !important;
            }
            .retenciones-form .ant-form-item-required::before {
              color: ${theme.colors.error} !important;
            }
            .retenciones-form .ant-input,
            .retenciones-form .ant-select-selector,
            .retenciones-form .ant-input-number {
              border-color: ${theme.colors.border} !important;
              border-radius: ${theme.borderRadius.md} !important;
              font-size: 13px !important;
              height: 32px !important;
              padding: 4px 11px !important;
            }
            .retenciones-form .ant-input-number-input {
              height: 30px !important;
              font-size: 13px !important;
            }
            .retenciones-form .ant-select-selector {
              height: 32px !important;
            }
            .retenciones-form .ant-select-selection-item,
            .retenciones-form .ant-select-selection-placeholder {
              line-height: 30px !important;
              font-size: 13px !important;
            }
            .retenciones-form .ant-input:focus,
            .retenciones-form .ant-select-selector:focus,
            .retenciones-form .ant-input-number:focus {
              border-color: ${theme.colors.primary} !important;
              box-shadow: 0 0 0 2px rgba(45, 92, 146, 0.2) !important;
            }
          `}</style>

          {/* Grid de 3 columnas - Información básica */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
            <Form.Item
              label={<FormLabel required>Descripción</FormLabel>}
              name="descripcion"
              rules={[
                { required: true, message: 'Por favor ingrese la descripción' },
                { max: 100, message: 'La descripción no puede exceder 100 caracteres' }
              ]}
            >
              <Input 
                placeholder="Ej: Retención IVA" 
              />
            </Form.Item>

            <Form.Item
              label={<FormLabel required>Tipo de Retención</FormLabel>}
              name="tipo"
              rules={[{ required: true, message: 'Por favor seleccione el tipo' }]}
            >
              <SearchableSelect 
                placeholder="Seleccionar tipo"
                options={[
                  { value: 'IVA', label: 'IVA' },
                  { value: 'GANANCIA', label: 'GANANCIA' },
                  { value: 'RENTAS', label: 'RENTAS' },
                  { value: 'OTROS', label: 'OTROS' }
                ]}
              />
            </Form.Item>

            <Form.Item
              label={<FormLabel>Provincia</FormLabel>}
              name="provincia"
            >
              <SearchableSelect 
                placeholder="Seleccionar provincia (opcional)"
                allowClear
                loading={loadingProvincias}
                options={provincias.map(provincia => ({
                  value: provincia.id,
                  label: provincia.provincia
                }))}
              />
            </Form.Item>
          </div>

          <Divider orientation="left" plain style={{ marginTop: '8px', marginBottom: '8px', fontSize: '13px' }}>
            Valores Monetarios
          </Divider>

          {/* Grid de 4 columnas - Valores monetarios */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px' }}>
            <Form.Item
              label={<FormLabel>Porcentaje (%)</FormLabel>}
              name="porcentaje"
            >
              <InputNumber
                style={{ width: '100%' }}
                placeholder="0.00"
                min={0}
                max={100}
                step={0.01}
                precision={2}
                formatter={value => `${value}%`}
                parser={value => value!.replace('%', '') as any}
              />
            </Form.Item>

            <Form.Item
              label={<FormLabel>Portón por Tonelada</FormLabel>}
              name="portonelada"
            >
              <InputNumber
                style={{ width: '100%' }}
                placeholder="0.00"
                min={0}
                step={0.01}
                precision={2}
                formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={value => value!.replace(/\$\s?|(,*)/g, '') as any}
              />
            </Form.Item>

            <Form.Item
              label={<FormLabel>Mínimo Portón</FormLabel>}
              name="minimo_porton"
            >
              <InputNumber
                style={{ width: '100%' }}
                placeholder="0.00"
                min={0}
                step={0.01}
                precision={2}
                formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={value => value!.replace(/\$\s?|(,*)/g, '') as any}
              />
            </Form.Item>

            <Form.Item
              label={<FormLabel>Mínimo Pesos</FormLabel>}
              name="minimo_pesos"
            >
              <InputNumber
                style={{ width: '100%' }}
                placeholder="0.00"
                min={0}
                step={0.01}
                precision={2}
                formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={value => value!.replace(/\$\s?|(,*)/g, '') as any}
              />
            </Form.Item>
          </div>

          {/* Grid de 3 columnas - Campos finales */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px', marginTop: '8px' }}>
            <Form.Item
              label={<FormLabel>Acumulado Ganancia</FormLabel>}
              name="acumulado_ganancia"
            >
              <InputNumber
                style={{ width: '100%' }}
                placeholder="0.00"
                min={0}
                step={0.01}
                precision={2}
                formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                parser={value => value!.replace(/\$\s?|(,*)/g, '') as any}
              />
            </Form.Item>

            <Form.Item
              label={
                <FormLabel required>
                  <Space>
                    Aplicar a
                    <Tooltip 
                      title="Seleccione a qué tipo de operación se aplicará la retención. COMPRA/SERVICIO: Se aplica a ambos, COMPRA: Solo a compras, SERVICIO: Solo a servicios."
                      placement="top"
                    >
                      <QuestionCircleOutlined style={{ color: theme.colors.primary, cursor: 'help' }} />
                    </Tooltip>
                  </Space>
                </FormLabel>
              }
              name="aplicar"
              rules={[{ required: true, message: 'Por favor seleccione a qué aplicar' }]}
            >
              <SearchableSelect 
                placeholder="Seleccionar aplicación"
                options={[
                  { value: 'COMPRA/SERVICIO', label: 'COMPRA/SERVICIO' },
                  { value: 'COMPRA', label: 'COMPRA' },
                  { value: 'SERVICIO', label: 'SERVICIO' }
                ]}
              />
            </Form.Item>

            <Form.Item
              label={<FormLabel required>Estado</FormLabel>}
              name="alta"
              rules={[{ required: true, message: 'Por favor seleccione el estado' }]}
            >
              <SearchableSelect 
                placeholder="Seleccionar estado"
                options={[
                  { value: 1, label: 'Activo' },
                  { value: 0, label: 'Inactivo' }
                ]}
              />
            </Form.Item>
          </div>

          {/* Botones de acción */}
          <Divider style={{ marginTop: '12px', marginBottom: '8px' }} />
          <div style={{ 
            display: 'flex', 
            justifyContent: 'flex-end', 
            gap: '6px',
            marginTop: '8px'
          }}>
            <Button
              icon={<ArrowLeftOutlined />}
              onClick={handleVolver}
              size="small"
              style={{
                color: theme.colors.textSecondary,
                borderColor: theme.colors.border,
                fontSize: '12px',
                height: '28px',
                padding: '0 12px'
              }}
            >
              Volver al Listado
            </Button>

            <Button
              onClick={handleLimpiar}
              size="small"
              style={{
                color: theme.colors.warning,
                borderColor: theme.colors.warning,
                fontSize: '12px',
                height: '28px',
                padding: '0 12px'
              }}
            >
              Restaurar Valores
            </Button>

            <Button
              type="primary"
              htmlType="submit"
              icon={<SaveOutlined />}
              loading={loading}
              size="small"
              style={{
                backgroundColor: theme.colors.primary,
                borderColor: theme.colors.primary,
                fontSize: '12px',
                height: '28px',
                padding: '0 12px'
              }}
            >
              Actualizar Retención
            </Button>
          </div>
        </Form>
      </StyledCard>
    </div>
  );
};

export default EditarRetenciones;
